import { useCallback, useEffect, useRef, useState } from 'react'

// Web Speech API speech-to-text. Push-to-talk: start() opens the mic, the live transcript streams
// into `transcript`, and onResult fires with the final text when the user stops speaking.
// Gracefully no-ops on browsers without SpeechRecognition (reported via `supported`).
type Recognition = any

function getRecognition(): Recognition | null {
  const w = window as any
  const Ctor = w.SpeechRecognition || w.webkitSpeechRecognition
  return Ctor ? new Ctor() : null
}

export function useSpeechInput(onResult: (text: string) => void) {
  const [listening, setListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const recRef = useRef<Recognition | null>(null)
  const supported = typeof window !== 'undefined' && !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)
  const onResultRef = useRef(onResult)
  onResultRef.current = onResult

  const stop = useCallback(() => {
    try { recRef.current?.stop() } catch { /* */ }
  }, [])

  const start = useCallback(() => {
    if (!supported || recRef.current) return
    const rec = getRecognition()
    if (!rec) return
    rec.lang = 'en-US'
    rec.interimResults = true
    rec.continuous = false
    let finalText = ''
    rec.onresult = (e: any) => {
      let interim = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const chunk = e.results[i][0].transcript
        if (e.results[i].isFinal) finalText += chunk
        else interim += chunk
      }
      setTranscript(finalText || interim)
    }
    rec.onerror = () => { /* surfaced via onend */ }
    rec.onend = () => {
      recRef.current = null
      setListening(false)
      const text = finalText.trim()
      setTranscript('')
      if (text) onResultRef.current(text)
    }
    recRef.current = rec
    setListening(true)
    setTranscript('')
    try { rec.start() } catch { recRef.current = null; setListening(false) }
  }, [supported])

  useEffect(() => () => { try { recRef.current?.abort?.() } catch { /* */ } }, [])

  return { supported, listening, transcript, start, stop, toggle: () => (listening ? stop() : start()) }
}
